/************************************************************************************

* UTILITY FUNCTIONALITIES

*************************************************************************************/
window.ON_CHANGE_DEBOUNCE_TIMER = 300

window.PUB_SUB_EVENTS = {
	cartUpdate: 'cart-update',
	quantityUpdate: 'quantity-update',
	optionValueSelectionChange: 'option-value-selection-change',
	variantChange: 'variant-change',
	cartError: 'cart-error',
	library: {
		carousel: 'carousel-loaded',
	},
	quickView: {
		open: 'quick-view-open',
	},
}

let subscribers = {}

function subscribe(eventName, callback) {
	if (subscribers[eventName] === undefined) {
		subscribers[eventName] = []
	}

	subscribers[eventName] = [...subscribers[eventName], callback]

	return function unsubscribe() {
		subscribers[eventName] = subscribers[eventName].filter((cb) => {
			return cb !== callback
		})
	}
}

function publish(eventName, data) {
	if (subscribers[eventName]) {
		subscribers[eventName].forEach((callback) => {
			callback(data)
		})
	}
}

/************************************************************************************

* UTILITY FUNCTIONS

*************************************************************************************/

function getFocusableElements(container) {
	return Array.from(
		container.querySelectorAll(
			"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
		)
	)
}

class SectionId {
	static #separator = '__'

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
	static parseId(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[0]
	}

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
	static parseSectionName(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[1]
	}

	// for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
	static getIdForSection(sectionId, sectionName) {
		return `${sectionId}${SectionId.#separator}${sectionName}`
	}
}

class HTMLUpdateUtility {
	/**
	 * Used to swap an HTML node with a new node.
	 * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
	 *
	 * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
	 */
	static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
		preProcessCallbacks?.forEach((callback) => callback(newContent))

		const newNodeWrapper = document.createElement('div')
		HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML)
		const newNode = newNodeWrapper.firstChild

		// dedupe IDs
		const uniqueKey = Date.now()
		oldNode.querySelectorAll('[id], [form]').forEach((element) => {
			element.id && (element.id = `${element.id}-${uniqueKey}`)
			element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`)
		})

		oldNode.parentNode.insertBefore(newNode, oldNode)
		oldNode.style.display = 'none'

		postProcessCallbacks?.forEach((callback) => callback(newNode))

		setTimeout(() => oldNode.remove(), 500)
	}

	// Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
	static setInnerHTML(element, html) {
		element.innerHTML = html
		element.querySelectorAll('script').forEach((oldScriptTag) => {
			const newScriptTag = document.createElement('script')
			Array.from(oldScriptTag.attributes).forEach((attribute) => {
				newScriptTag.setAttribute(attribute.name, attribute.value)
			})
			newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
			oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
		})
	}
}

const trapFocusHandlers = {}

function trapFocus(container, elementToFocus = container) {
	var elements = getFocusableElements(container)
	var first = elements[0]
	var last = elements[elements.length - 1]

	removeTrapFocus()

	trapFocusHandlers.focusin = (event) => {
		if (event.target !== container && event.target !== last && event.target !== first) return

		document.addEventListener('keydown', trapFocusHandlers.keydown)
	}

	trapFocusHandlers.focusout = function () {
		document.removeEventListener('keydown', trapFocusHandlers.keydown)
	}

	trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== 'TAB') return // If not TAB key
		// On the last focusable element and tab forward, focus the first element.
		if (event.target === last && !event.shiftKey) {
			event.preventDefault()
			first.focus()
		}

		//  On the first focusable element and tab backward, focus the last element.
		if ((event.target === container || event.target === first) && event.shiftKey) {
			event.preventDefault()
			last.focus()
		}
	}

	document.addEventListener('focusout', trapFocusHandlers.focusout)
	document.addEventListener('focusin', trapFocusHandlers.focusin)

	elementToFocus.focus()

	if (
		elementToFocus.tagName === 'INPUT' &&
		['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
		elementToFocus.value
	) {
		elementToFocus.setSelectionRange(0, elementToFocus.value.length)
	}
}

function focusVisiblePolyfill() {
	const navKeys = [
		'ARROWUP',
		'ARROWDOWN',
		'ARROWLEFT',
		'ARROWRIGHT',
		'TAB',
		'ENTER',
		'SPACE',
		'ESCAPE',
		'HOME',
		'END',
		'PAGEUP',
		'PAGEDOWN',
	]
	let currentFocusedElement = null
	let mouseClick = null

	window.addEventListener('keydown', (event) => {
		if (navKeys.includes(event.code.toUpperCase())) {
			mouseClick = false
		}
	})

	window.addEventListener('mousedown', (event) => {
		mouseClick = true
	})

	window.addEventListener(
		'focus',
		() => {
			if (currentFocusedElement) currentFocusedElement.classList.remove('focused')

			if (mouseClick) return

			currentFocusedElement = document.activeElement
			currentFocusedElement.classList.add('focused')
		},
		true
	)
}

function pauseAllMedia() {
	document.querySelectorAll('.js-youtube').forEach((video) => {
		video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*')
	})
	document.querySelectorAll('.js-vimeo').forEach((video) => {
		video.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	document.querySelectorAll('video').forEach((video) => video.pause())
	document.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
}

function removeTrapFocus(elementToFocus = null) {
	document.removeEventListener('focusin', trapFocusHandlers.focusin)
	document.removeEventListener('focusout', trapFocusHandlers.focusout)
	document.removeEventListener('keydown', trapFocusHandlers.keydown)

	if (elementToFocus) elementToFocus.focus()
}

function onKeyUpEscape(event) {
	if (event.code.toUpperCase() !== 'ESCAPE') return

	const openDetailsElement = event.target.closest('details[open]')
	if (!openDetailsElement) return

	const summaryElement = openDetailsElement.querySelector('summary')
	openDetailsElement.removeAttribute('open')
	summaryElement.setAttribute('aria-expanded', false)
	summaryElement.focus()
}

function debounce(fn, wait) {
	let t
	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}

function throttle(fn, delay) {
	let lastCall = 0
	return function (...args) {
		const now = new Date().getTime()
		if (now - lastCall < delay) {
			return
		}
		lastCall = now
		return fn(...args)
	}
}

function fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}

function initLazyScript(element, callback, threshold = 500) {
	if ('IntersectionObserver' in window) {
		const io = new IntersectionObserver(
			(entries, observer) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						if (typeof callback === 'function') {
							callback()
							observer.unobserve(entry.target)
						}
					}
				})
			},
			{ rootMargin: `0px 0px ${threshold}px 0px` }
		)

		io.observe(element)
	} else {
		callback()
	}
}

function formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separator':
			value = formatWithDelimiters(cents, 2, '.', '.')
			break
		case 'amount_no_decimals_with_comma_separator':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}

function getSectionDOM(html, selector = '.shopify-section') {
	return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
}

function updateHeaderCSSVariables() {
	var headerSection = headerSection || document.querySelector('.section-header')
	var announcementBarSection = announcementBarSection || document.querySelector('.section-announcement-bar')

	document.documentElement.style.setProperty('--l-viewport-height', `${visualViewport?.height ?? window.innerHeight}px`)
	document.documentElement.style.setProperty('--l-header-height', `${headerSection?.scrollHeight || 0}px`)
	document.documentElement.style.setProperty(
		'--l-announcement-bar-height',
		`${announcementBarSection?.scrollHeight || 0}px`
	)
}

function lockBodyScroll() {
	document.documentElement.classList.add('js-block-page')
}

function unLockBodyScroll() {
	document.documentElement.classList.remove('js-block-page')
}

/************************************************************************************

COMPONENT: DEFERRED-MEDIA

*************************************************************************************/
if (!customElements.get('l-deferred-media')) {
	customElements.define(
		'l-deferred-media',
		class LTADeferredMedia extends HTMLElement {
			constructor() {
				super()
				const poster = this.querySelector('[id^="Deferred-Poster-"]')

				this.videoEventsHandler()

				if (!poster) return
				poster.addEventListener('click', this.loadContent.bind(this))
			}

			loadContent(focus = true) {
				window.pauseAllMedia()
				if (!this.getAttribute('loaded')) {
					const content = document.createElement('div')
					content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true))

					this.setAttribute('loaded', true)
					const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'))
					if (focus) deferredElement.focus()
					if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
						// force autoplay for safari
						deferredElement.play()
						this.videoEventsHandler()
					}
				}
			}

			videoEventsHandler() {
				this.videoEl = this.querySelector('video')

				this.videoEl.addEventListener('play', () => {
					this.classList.remove('is-paused')
				})

				this.videoEl.addEventListener('pause', () => {
					this.classList.add('is-paused')
				})

				this.videoEl.addEventListener('ended', () => {
					this.classList.add('is-paused')
				})
			}
		}
	)
}

/************************************************************************************

* GENERAL COMPONENTS

*************************************************************************************/
if (!customElements.get('l-collapsible-tab')) {
	customElements.define(
		'l-collapsible-tab',
		class LTACollapsibleTab extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.init()
			}

			init() {
				this.setDefaultData()
				this.attachEvents()

				if (this.getAttribute('open') === 'true') {
					this.selected = true
					this.classList.add(this.expandedClass)
					this.setExpandedAria()
				} else {
					this.content.style.height = this.collapsedHeight
					this.classList.add(this.collapsedClass)
					this.setCollapsedAria()
				}

				this.content.removeAttribute('hidden')
			}

			setDefaultData() {
				this.expandedClass = 'is-expanded'
				this.collapsedClass = 'is-collapsed'

				this.transitionendEvent = this.transitionendEventName()

				this.trigger = this.querySelector('[data-trigger]')
				this.content = this.querySelector('[data-content]')

				this.collapsedHeight = '0px'
				this.defaultElements = ['a', 'button', 'input:not(.focus-none)', '[data-trigger]']
				this.oneAtATime = true

				this.dataset.oneOpen === 'false' && (this.oneAtATime = false)
			}

			attachEvents() {
				this.trigger.addEventListener('click', (e) => this.toggle(e))
			}

			toggle(e) {
				e.preventDefault()

				this.selected ? this.close() : this.open()

				/* Close another tab and open only the current tab. */
				if (this.selected && this.oneAtATime) {
					const tabsContainer = this.closest('[data-collapsible-wrapper]')
					const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

					collapsibleTabs?.length &&
						collapsibleTabs.forEach((tab) => {
							tab !== this && tab.selected && tabsContainer !== tab && tab.close()
						})
				}
			}

			open() {
				this.selected = true
				this.expand()
				this.setAttribute('open', true)
			}

			close() {
				this.selected = false
				this.collapse()
				this.removeAttribute('open')
			}

			expand() {
				const onTransitionEnd = ((event) => {
					if (event.target === this.content) {
						this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

						this.isOpen &&
							requestAnimationFrame(() => {
								this.content.style.transition = '0'
								this.content.style.height = 'auto'

								requestAnimationFrame(() => {
									this.content.style.height = null
									this.content.style.transition = null
									this.setExpandedAria()
									this.trySetTabIndex(this.content, 0)
								})
							})
					}
				}).bind(this)

				this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
				this.isOpen = true
				this.classList.add(this.expandedClass)
				this.classList.remove(this.collapsedClass)
				this.content.style.height = this.content.scrollHeight + 'px'
			}

			collapse() {
				const onTransitionEnd = ((event) => {
					if (event.target === this.content) {
						this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

						if (!this.isOpen) {
							this.setCollapsedAria()
							this.classList.add(this.collapsedClass)
							this.trySetTabIndex(this.content, -1)
						}
					}
				}).bind(this)

				this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
				this.isOpen = false
				this.classList.remove(this.expandedClass)

				requestAnimationFrame(() => {
					this.content.style.transition = '0'
					this.content.style.height = this.content.scrollHeight + 'px'

					requestAnimationFrame(() => {
						this.content.style.transition = null
						this.content.style.height = this.collapsedHeight
					})
				})
			}

			destroy() {
				this.trigger && this.trigger.removeEventListener('click', (e) => this.toggle(e))
				this.content && this.content.removeAttribute('aria-hidden')
				this.content && (this.content.style.height = 'auto')

				this.classList.remove(this.expandedClass, this.collapsedClass)
				this.removeAttribute('open')
			}

			transitionendEventName() {
				let eventName,
					testElement = document.createElement('div'),
					eventNames = {
						transition: 'transitionend',
						OTransition: 'otransitionend',
						MozTransition: 'transitionend',
						WebkitTransition: 'webkitTransitionEnd',
					}

				for (eventName in eventNames) {
					if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
						return eventNames[eventName]
					}
				}
			}

			trySetTabIndex(parentElement, tabIndex) {
				const elements = parentElement.querySelectorAll(this.defaultElements)

				elements &&
					elements.forEach((e) => {
						e.setAttribute('tabindex', tabIndex)
					})
			}

			setExpandedAria() {
				this.trigger.setAttribute('aria-expanded', 'true')
				this.content.setAttribute('aria-hidden', 'false')
			}

			setCollapsedAria() {
				this.trigger.setAttribute('aria-expanded', 'false')
				this.content.setAttribute('aria-hidden', 'true')
			}
		}
	)
}

if (!customElements.get('l-swiper-carousel')) {
	customElements.define(
		'l-swiper-carousel',
		class LTASwiperCarousel extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Check if Swiper is already loaded
				if (window.Swiper) {
					this.setupCarousel()

					return
				}

				document.addEventListener('carousel:loaded', () => {
					this.setupCarousel()
				})
			}

			setupCarousel() {
				this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

				if (!this.carouselConfig) return

				this.classList.remove('is-placeholder')
				this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

        this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
			}

			slideTo(index = 0) {
				this.swiperCarousel.slideTo(index)
			}
		}
	)
}


class LTADrawer extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.setupDrawer()
	}

	setupDrawer() {
		// Elements
		this.triggerEls = document.querySelectorAll(this.dataset.triggerSelector)
		this.closeBtnEls = this.querySelectorAll('[data-close]')
		this.drawerInnerEl = this.querySelector('[data-drawer-inner]')
    this.triggerEl = null

    console.log(this.triggerEls);

		// Data
		this.onlyOneDrawerOpen = this.dataset.onlyOneDrawerOpen === 'true'
		this.drawerName = this.dataset.drawerName || 'drawer'

		// Setup events

		// Close this drawer if another drawer is open
		if (this.onlyOneDrawerOpen) {
			document.addEventListener('l:drawer:open', (event) => {
				if (event.detail.drawerName !== this.drawerName) {
					this.drawer_close()
				}
			})
		}

		// Open event
		this.triggerEls.forEach((triggerEl) => {
			triggerEl.addEventListener('click', (event) => {
				event.preventDefault()

				this.drawer_open(triggerEl)
			})
		})

		// Close event
		this.closeBtnEls.forEach((closeBtnEl) => {
			closeBtnEl.addEventListener('click', () => {
				this.drawer_close()
			})
		})
	}

	drawer_open(triggerEl) {
		updateHeaderCSSVariables()
		lockBodyScroll(this.drawerName)

		// Open drawer
		this.classList.remove('l-hidden')

		requestAnimationFrame(() => {
			this.classList.add('is-active')
		})

		// Setup focus trap on main content
		if (triggerEl) {
			this.triggerEl = triggerEl
			trapFocus(this.drawerInnerEl)
		}

		// Set global events
		window.addEventListener('click', this.drawer_closeOutsideOnClick.bind(this))
		window.addEventListener('keyup', this.drawer_closeOutsideOnEscape.bind(this))

		// Dispatch event to notify that the drawer is open
		document.dispatchEvent(
			new CustomEvent('l:drawer:open', {
				detail: { drawerName: this.drawerName },
			})
		)
	}

	drawer_close() {
		unLockBodyScroll(this.drawerName)

		// Close drawer
		this.classList.remove('is-active')

		window.debounce(() => {
			this.classList.add('l-hidden')
		}, 400)()

		// Remove focus trap
		if (this.triggerEl) {
			removeTrapFocus(this.triggerEl)
			this.triggerEl = null
		}

		// Remove global events
		window.removeEventListener('click', this.drawer_closeOutsideOnClick)
		window.removeEventListener('keyup', this.drawer_closeOutsideOnEscape)
	}

	drawer_closeOutsideOnClick(event) {
		const target = event.target
		const isDrawerTrigger = event.target.closest('l-drawer-trigger') || event.target.tagName == 'l-drawer-trigger'

		const isOutsideClick =
			!this.drawerInnerEl.contains(target) &&
			!Array.from(this.triggerEls).some((el) => el.contains(target)) &&
			!isDrawerTrigger

		if (isOutsideClick) this.drawer_close()
	}

	drawer_closeOutsideOnEscape(event) {
		if (event.code.toUpperCase() === 'ESCAPE') {
			this.drawer_close()
		}
	}
}

customElements.define('l-drawer', LTADrawer)

/************************************************************************************

COMPONENT: SWIPER CAROUSEL

*************************************************************************************/
if (!customElements.get('l-product-card')) {
	customElements.define(
		'l-product-card',
		class LTAProductCard extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.addToCartBtnEl = this.querySelector('[data-product-card-atc]')
				this.quickViewBtnEl = this.querySelector('[data-product-card-quick-view]')

				this.addToCartBtnEl && this.addToCartEvent()
				this.quickViewBtnEl && this.quickViewEvent()
			}

			addToCartEvent() {
				this.cart = document.getElementById('nav-drawer')

				this.addToCartBtnEl.addEventListener('click', (event) => {
					event.preventDefault()

					this.addToCartBtnEl.classList.add('is-loading')

					const config = fetchConfig('javascript')
					config.headers['X-Requested-With'] = 'XMLHttpRequest'
					delete config.headers['Content-Type']

					const formData = new FormData()
					formData.append('quantity', '1')
					formData.append('id', this.addToCartBtnEl.dataset.productCardAtc)

					config.body = formData

					// Fetch API to ATC
					fetch(`${window.theme.routes.cart_add_url}`, config)
						.then((response) => response.json())
						.then((response) => {
							// ATC: Handle error case
							if (response.status) {
								return
							}

							if (!this.cart) {
								// Redirect to cart page if cart-drawer equal to null
								window.location = window.theme.routes.cart_url

								return
							}

							window.getCart?.()
							window.scrollToCartTop?.()

							publish(PUB_SUB_EVENTS.cartUpdate, {
								source: 'product-form',
								productVariantId: formData.get('id'),
								cartData: response,
							})

							document.dispatchEvent(
								new CustomEvent('theme:cart:add', {
									bubbles: true,
									detail: {
										selector: this.addToCartBtnEl,
									},
								})
							)

							this.error = false
						})
						.catch((e) => {
							console.error(e)
						})
						.finally(() => {
							// Remove button loading state
							this.addToCartBtnEl.classList.remove('is-loading')
						})
				})
			}

			quickViewEvent() {
				this.productUrl = this.quickViewBtnEl.dataset.productCardQuickView

				this.quickViewBtnEl.addEventListener('click', () => {
					this.classList.add('is-loading')

					publish(PUB_SUB_EVENTS.quickView.open, {
						source: 'l-product-card',
						productUrl: this.productUrl,
					})

					debounce(() => {
						this.classList.remove('is-loading')
					}, 2000)()
				})
			}
		}
	)
}

/************************************************************************************

COMPONENT: SECTION SWATCH VIDEO

*************************************************************************************/
if (!customElements.get('l-section-video')) {
	customElements.define(
		'l-section-video',
		class LTASectionWatchVideo extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.playButtonEl = this.querySelector('[data-play-video-btn]')
				this.playButtonLabelEl = this.querySelector('[data-button-label]')
				this.playIconEl = this.querySelector('[data-play-icon]')
				this.pauseIconEl = this.querySelector('[data-pause-icon]')
				this.templateEl = this.querySelector('template')
				this.sectionContentEl = this.querySelector('[data-section-content]')
				this.sectionMediaEl = this.querySelector('[data-section-media]')

				// Data
				this.isVideoLoaded = false

				if (!this.playButtonEl) return

				this.playButtonEl.addEventListener('click', (event) => {
					event.preventDefault()

					if (this.templateEl && this.isVideoLoaded) {
						this.videoEl.paused ? this.playVideo() : this.pauseVideo()

						return
					}

					this.templateEl ? this.setInternalVideo() : this.setupYoutubeVideo()
				})
			}

      setInternalVideo() {
        const stopEvents = ['pause', 'ended']
        const videoTemplate = this.templateEl.content.cloneNode(true)

				this.videoEl = videoTemplate.querySelector('video, iframe')
				this.sectionMediaEl.appendChild(this.videoEl)
        this.isVideoLoaded = true

				this.videoEl.focus()

				if (this.videoEl.tagName.toLowerCase() === 'video') {
					this.sectionContentEl.classList.add('is-playing')
          this.playVideo()

          stopEvents.forEach((event) => {
            this.videoEl.addEventListener(event, () => {
              this.sectionContentEl.classList.remove('is-playing')
              this.pauseVideo()
            })
          })

          this.videoEl.addEventListener('play', () => {
            this.sectionContentEl.classList.add('is-playing')
            this.playVideo(false)
          })
				}
			}

			setupYoutubeVideo() {
				this.sectionContentEl.classList.add('l-hidden')

				if (this.youtubePlayer) {
					this.classList.remove('is-hide-youtube')
					this.youtubePlayer.playVideo()

					return
				}

				this.youtubePlayer = new YT.Player(this.dataset.sectionId, {
					videoId: this.dataset.youtubeVideoId,
					events: {
						onReady: (event) => {
							event.target.playVideo()
						},
						onStateChange: (event) => {
							if (event.data == YT.PlayerState.ENDED) {
								this.sectionContentEl.classList.remove('l-hidden')
								this.classList.add('is-hide-youtube')
							}
						},
					},
				})
			}

			pauseVideo() {
				this.videoEl.pause()
				this.playButtonLabelEl.textContent = this.playButtonLabelEl.dataset.buttonLabel
				this.playIconEl.classList.remove('l-hidden')
				this.pauseIconEl.classList.add('l-hidden')
			}

			playVideo(isPlay = true) {
				isPlay && this.videoEl.play()
				this.playButtonLabelEl.textContent = window.theme.strings.pause_video || 'Pause Video'
				this.playIconEl.classList.add('l-hidden')
				this.pauseIconEl.classList.remove('l-hidden')
			}
		}
	)
}


/************************************************************************************

COMPONENT: SECTION SETUP OPTION CARDS

*************************************************************************************/
if (!customElements.get('l-setup-option-cards')) {
	customElements.define(
		'l-setup-option-cards',
		class LTASetupOptionCards extends HTMLElement {
			constructor() {
				super()
			}

      connectedCallback() {
        this.triggerMediaBtnEls = this.querySelectorAll('[data-trigger-guide-btn]')
        this.mediaEls = this.querySelectorAll('[data-option-media]')
        this.optionGuideTitleEls = this.querySelectorAll('[data-option-guide-cards-title]')
        this.optionGuideCardEls = this.querySelectorAll('[data-option-guide-cards]')


        this.changeMedia()
			}

      changeMedia() {
        this.triggerMediaBtnEls.forEach((triggerMediaBtnEl) => {
          triggerMediaBtnEl.addEventListener('click', (event) => {
            event.preventDefault()

            const mediaId = triggerMediaBtnEl.dataset.triggerGuideBtn

            this.triggerMediaBtnEls.forEach((_triggerMediaBtnEl) => {
              _triggerMediaBtnEl.classList.toggle('is-active', _triggerMediaBtnEl == triggerMediaBtnEl)
            })


            pauseAllMedia()

            // Change option guide: title + cards
            this.optionGuideTitleEls.forEach((optionGuideTitleEl) => {
              optionGuideTitleEl.classList.toggle('l-hidden', optionGuideTitleEl.dataset.optionGuideCardsTitle != mediaId)
            })
            this.optionGuideCardEls.forEach((optionGuideCardEl) => {
              optionGuideCardEl.classList.toggle('l-hidden', optionGuideCardEl.dataset.optionGuideCards != mediaId)
            })


            // Change media
            this.mediaEls.forEach((mediaEl) => {
              mediaEl.classList.toggle('l-hidden', mediaEl.dataset.optionMedia != mediaId)

              if (mediaEl.dataset.optionMedia == mediaId) {
                const videoEl = mediaEl.querySelector('video')
                // videoEl && videoEl.play()

                if (!videoEl) {
                  const content = document.createElement('div')
                  content.appendChild(mediaEl.querySelector('template').content.firstElementChild.cloneNode(true))
                  const deferredElement = mediaEl.appendChild(content.querySelector('video'))
                }

                // mediaEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }
            })
          })
        })
      }
		}
	)
}

/************************************************************************************

COMPONENT: SECTION PRODUCT RECOMMENDATIONS

*************************************************************************************/
if (!customElements.get('l-product-recommendations')) {
	customElements.define(
		'l-product-recommendations',
		class LTAProductRecommendations extends HTMLElement {
			observer = undefined

			constructor() {
				super()
			}

			connectedCallback() {
				this.initializeRecommendations(this.dataset.productId)
			}

			initializeRecommendations(productId) {
				this.observer?.unobserve(this)
				this.observer = new IntersectionObserver(
					(entries, observer) => {
						if (!entries[0].isIntersecting) return
						observer.unobserve(this)
						this.loadRecommendations(productId)
					},
					{ rootMargin: '0px 0px 400px 0px' }
				)
				this.observer.observe(this)
			}

			loadRecommendations(productId) {
				fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
					.then((response) => response.text())
					.then((text) => {
						const html = document.createElement('div')
						html.innerHTML = text
						const recommendations = html.querySelector('l-product-recommendations')

						if (recommendations?.innerHTML.trim().length) {
							this.innerHTML = recommendations.innerHTML
						}
					})
					.catch((e) => {
						console.error(e)
					})
			}
		}
	)
}

/************************************************************************************

SECTION: TEXT CAROUSEL IMAGES

*************************************************************************************/
if (!customElements.get('l-text-carousel-images')) {
	customElements.define(
		'l-text-carousel-images',
		class LTATextCarouselImages extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Check if Swiper is already loaded
				if (window.Swiper) {
					this.setupCarousel()

					return
				}

				document.addEventListener('carousel:loaded', () => {
					this.setupCarousel()
				})
			}

			setupCarousel() {
				// Media swiper
				const mediaSwiperEl = this.querySelector('[data-media-swiper]')
				const mediaCarouselConfig = JSON.parse(mediaSwiperEl.querySelector('[data-carousel-config]').textContent)

				mediaSwiperEl.classList.remove('is-placeholder')
				mediaSwiperEl
					.querySelectorAll('.swiper-slide')
					.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.mediaSwiper = new window.Swiper(mediaSwiperEl, {
					...mediaCarouselConfig,
					on: {
						init: (swiper) => {
							swiper.autoplay.pause()

							this.onAutoPlayOnHover()
						},
					},
				})

				// Content swiper
				const contentSwiperEl = this.querySelector('[data-content-swiper]')
				const contentCarouselConfig = JSON.parse(contentSwiperEl.querySelector('[data-carousel-config]').textContent)

				contentSwiperEl.classList.remove('is-placeholder')
				contentSwiperEl
					.querySelectorAll('.swiper-slide')
					.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.contentSwiper = new window.Swiper(contentSwiperEl, contentCarouselConfig)

				// Link controller
				this.mediaSwiper.controller.control = this.contentSwiper
			}

			onAutoPlayOnHover() {
				const observer = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => {
							if (entry.isIntersecting) {
								this.mediaSwiper.autoplay.start()
							} else {
								this.mediaSwiper.autoplay.stop()
							}
						})
					},
					{ threshold: 0.5 }
				)

				observer.observe(this)
			}

			slideTo(index = 0) {
				this.swiperCarousel.slideTo(index)
			}
		}
	)
}

/************************************************************************************

SECTION: MAIN BLOG

*************************************************************************************/
if (!customElements.get('l-main-blog')) {
	customElements.define(
		'l-main-blog',
		class LTAMainBlog extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.loadMoreBtnEl = this.querySelector('[data-load-more]')
        this.blogGridEl = this.querySelector('[data-blog-grid]')
        this.loadMoreType = this.dataset.loadMoreType || 'button'

        // Set up events
				this.addAnchorToBlogTags()

				if (this.loadMoreBtnEl && this.blogGridEl) {
					this.currentPage = parseInt(this.dataset.currentPage) || 1
          this.maxPages = parseInt(this.dataset.maxPages) || 1


          if (this.loadMoreType === 'button') {
            this.loadMoreBtnEl.addEventListener('click', this.loadMorePosts.bind(this))
          }


          if (this.loadMoreType === 'scroll') {
            window.addEventListener(
              'scroll',
              debounce(() => {
                const sectionBottom = this.getBoundingClientRect().bottom
                const windowBottom = window.innerHeight

                if (windowBottom + 500 >= sectionBottom && this.currentPage < this.maxPages) {
                  this.loadMorePosts()
                }
              }, 30)
            )
          }
        }
			}


      loadMorePosts() {
        const url = `${this.dataset.url}?page=${this.currentPage + 1}&section_id=${this.dataset.sectionId}`

				this.loadMoreBtnEl.classList.add('is-loading')

				fetch(url)
					.then((response) => response.text())
					.then((text) => {
						const html = document.createElement('div')
						html.innerHTML = text
						const newPosts = html.querySelector('[data-blog-grid]').innerHTML

						// Preload images
						const tempDiv = document.createElement('div')
						tempDiv.innerHTML = newPosts
						const images = tempDiv.querySelectorAll('img')
						images.forEach((img) => {
							const preloadedImg = new Image()
							preloadedImg.src = img.src
						})

						this.blogGridEl.insertAdjacentHTML('beforeend', tempDiv.innerHTML)

						this.currentPage += 1

						if (this.currentPage >= this.maxPages) {
							this.loadMoreBtnEl.classList.add('l-hidden')
						}
					})
					.finally(() => {
            this.loadMoreBtnEl.classList.remove('is-loading')
					})
			}

			addAnchorToBlogTags() {
				const tagLinkEls = this.querySelectorAll('[data-tags] a')

				tagLinkEls.forEach((tag) => {
					tag.setAttribute('href', `${tag.getAttribute('href')}#mainBlog`)
				})
			}
		}
	)
}

/************************************************************************************

SECTION: RIDER PROFILES

*************************************************************************************/
if (!customElements.get('l-rider-profiles')) {
	customElements.define(
		'l-rider-profiles',
		class LTARiderProfiles extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.bottomImageEls = this.querySelectorAll('[data-bottom-image]')
				this.swiperCarouselEls = this.querySelectorAll('l-swiper-carousel')
				this.profileEls = this.querySelectorAll('[data-profile]')

				this.changeProfileEls = this.querySelectorAll('[data-change-profile-btn]')
				this.paginationBulletEls = this.querySelectorAll('[data-pagination-bullet]')

				// Events
				this.setupZoomFeature()
				this.setupNextSlide()
			}

			setupZoomFeature() {
				this.bottomImageEls.forEach((bottomImageEl) => {
					new window.mediaZoom(bottomImageEl)
				})
			}

			setupNextSlide() {
				this.changeProfileEls.forEach((changeProfileEl) => {
					changeProfileEl.addEventListener('click', () => {
						const nextAthleteIndex = parseInt(changeProfileEl.dataset.changeProfileBtn)

						this.slideTo(nextAthleteIndex)
					})
				})

				this.paginationBulletEls.forEach((paginationBulletEl) => {
					paginationBulletEl.addEventListener('click', () => {
						const nextSlideIndex = parseInt(paginationBulletEl.dataset.paginationBullet)

						this.slideTo(nextSlideIndex)
					})
				})
			}

			slideTo(index = 0) {
				this.swiperCarouselEls.forEach((swiperCarouselEl) => {
					swiperCarouselEl.slideTo(index)
				})

				this.profileEls.forEach((profileEl) => {
					const profileIndex = parseInt(profileEl.dataset.profile)

					profileEl.classList.toggle('is-active', profileIndex === index)
				})

				debounce(() => {
					window.scrollTo({ top: 0, behavior: 'smooth' })
				}, 400)()
			}
		}
	)
}

if (!customElements.get('l-single-rider-profile')) {
	customElements.define(
		'l-single-rider-profile',
		class LTASingleRiderProfile extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
        // Elements
        this.mediaGroupEls = this.querySelectorAll('[data-media-zoom]')
        this.thumbnailTriggerEls = this.querySelectorAll('[data-trigger-main-media]')
        this.mainMediaEls = this.querySelectorAll('[data-main-media]')
        this.profileEls = this.querySelectorAll('[data-profile]')


				// Events
        this.setupZoomFeature()

        document.addEventListener('carousel:loaded', () => {
          this.setupCarouselEvents()
        })
			}

      setupCarouselEvents() {
        this.mainMediaEls.forEach((mainMediaEl) => {
          const carouselConfig = JSON.parse(mainMediaEl.querySelector('[data-carousel-config]').textContent)

          const mainSwiperCarousel = new window.Swiper(mainMediaEl, carouselConfig)

          mainSwiperCarousel.on('slideChange', (swiper) => {
            const activeSlideIndex = parseInt(swiper.activeIndex)

            this.thumbnailTriggerEls.forEach((thumbnailTriggerEl, index0) => {
              thumbnailTriggerEl.classList.toggle('is-active', index0 == activeSlideIndex)
            })

            this.profileEls.forEach((profileEl, index0) => {
              profileEl.classList.toggle('l-hidden', index0 != activeSlideIndex)
            })
          })

          // Setup click thumbnail event
          this.thumbnailTriggerEls.forEach((thumbnailTriggerEl) => {
            const slideIndex = thumbnailTriggerEl.dataset.triggerMainMedia

            thumbnailTriggerEl.addEventListener('click', () => {
              // Change main slide
              mainSwiperCarousel.slides.forEach((slide, index0) => {
                const dataMedia = slide.dataset.mediaId
                if (slideIndex == dataMedia) mainSwiperCarousel.slideTo(index0)
              })

              // Add active class
              this.thumbnailTriggerEls.forEach((_thumbnailTriggerEl) => {
                _thumbnailTriggerEl.classList.toggle('is-active', _thumbnailTriggerEl === thumbnailTriggerEl)
              })
            })
          })
        })
      }

      setupZoomFeature() {
        this.mediaGroupEls.forEach((mediaGroupEl) => {
          new window.mediaZoom(mediaGroupEl)
        })
      }
		}
	)
}

/************************************************************************************

SECTION: TIMELINE JOURNEY

*************************************************************************************/
if (!customElements.get('l-timeline-journey')) {
	customElements.define(
		'l-timeline-journey',
		class LTATimelineJourney extends HTMLElement {
			constructor() {
				super()
			}

      connectedCallback() {
        document.addEventListener('gsap:loaded', () => {
          this.setupGsap()
        })
      }

      setupGsap() {
        gsap.registerPlugin(ScrollTrigger, Draggable)

        const sections = this.querySelectorAll('[data-timeline-journey]')
        const wrapper = this.offsetHeight < window.innerHeight ? this : this.querySelector('[data-wrapper]')
        const dragProxy = this.querySelector('[data-drag-proxy]')
        const desktop = window.matchMedia('(min-width: 1024px)').matches

        let maxWidth = 0

        const getMaxWidth = () => {
          maxWidth = 0
          sections.forEach((section) => {
            maxWidth += section.offsetWidth + (desktop ? 10 : 6)
          })
        }
        getMaxWidth()
        ScrollTrigger.addEventListener("refreshInit", getMaxWidth)

        let scrollTween = gsap.to(sections, {
          x: () => `-${maxWidth - window.innerWidth}`,
          ease: "none"
        })

        let horizontalScroll = ScrollTrigger.create({
          animation: scrollTween,
          trigger: wrapper,
          pin: true,
          scrub: true,
          end: () => `+=${maxWidth}`,
          invalidateOnRefresh: true,
          onRefresh: (self) => {
            self.refresh()
          },
        })

        sections.forEach((sct, i) => {
          ScrollTrigger.create({
            trigger: sct,
            start: () =>
              "top top-=" +
              (sct.offsetLeft - window.innerWidth / 2) * (maxWidth / (maxWidth - window.innerWidth)),
            end: () => "+=" + sct.offsetWidth * (maxWidth / (maxWidth - window.innerWidth)),
            toggleClass: { targets: sct, className: "active" },
            onRefresh: (self) => {
              self.refresh()
            }
          })
        })

        const dragRatio = maxWidth / (maxWidth - window.innerWidth)

        const drag = Draggable.create(dragProxy, {
          trigger: wrapper,
          type: "x",
          inertia: true,
          allowContextMenu: true,
          onPress() {
            this.startScroll = horizontalScroll.scroll()
          },
          onDrag() {
            horizontalScroll.scroll(this.startScroll - (this.x - this.startX) * dragRatio)
          },
          onThrowUpdate() {
            horizontalScroll.scroll(this.startScroll - (this.x - this.startX) * dragRatio)
          }
        })[0]
      }

      carouselEventHandler() {
        // Check if Swiper is already loaded
				if (window.Swiper) {
					this.setupCarousel()

					return
				}

				document.addEventListener('carousel:loaded', () => {
					this.setupCarousel()
				})
      }

			setupCarousel() {
				this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

				if (!this.carouselConfig) return

				this.classList.remove('is-placeholder')
				this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.swiperCarousel = new window.Swiper(this, this.carouselConfig)

				this.swiperCarousel.on('slideChange', (swiper) => {
					if (swiper.progress == 1) {
						const react = this.getBoundingClientRect()
						const sectionPosition = react.bottom + window.scrollY

						window.scrollTo({ top: sectionPosition, behavior: 'smooth' })

						return
					}

					if (swiper.progress == 0) {
						const sectionPosition = this.scrollTop + window.scrollY

						window.scrollTo({ top: sectionPosition, behavior: 'smooth' })
					}
				})
			}

			slideTo(index = 0) {
				this.swiperCarousel.slideTo(index)
			}
		}
	)
}


/************************************************************************************

COMPONENT: SECTION SCROLL BANNER

*************************************************************************************/
if (!customElements.get('l-scroll-banner')) {
	customElements.define(
		'l-scroll-banner',
		class LTAScrollBanner extends HTMLElement {
			constructor() {
				super()
			}

      connectedCallback() {
        if (window.gsap) {
          this.setupGsap()

          return
        }

        document.addEventListener('gsap:loaded', () => {
          this.zoomVideo()
        })
      }

      zoomVideo() {
        const contentEl = this.querySelector('.js-section-scroll-trigger-content-desktop')
        const mediaComponentEl = this.querySelector('.js-section-scroll-trigger-media-desktop')
        const videoEl = mediaComponentEl.querySelector('.l-media-ratio')

        gsap.fromTo(
          videoEl,
          {
            scale: 1,
            width: '80%',
          },
          {
            scrollTrigger: {
              trigger: contentEl,
              start: () => (contentEl.offsetHeight < window.innerHeight ? 'clamp(top top)' : 'clamp(top 60%)'),
              pin: true,
              pinSpacing: false,
              scrub: true,
              toggleActions: 'play pause resume reset',
              onRefresh: (self) => {
                self.refresh()
              },
              onLeave: () => {
                mediaComponentEl.style.setProperty('--l-media-content-position', 'absolute')
                mediaComponentEl.style.setProperty('--l-media-content-opacity', 0)
              },
              onEnterBack: () => {
                mediaComponentEl.style.setProperty('--l-media-content-position', 'fixed')
                mediaComponentEl.style.setProperty('--l-media-content-opacity', 1)
              },
            },
            scale: 1,
            width: '100%',
            onStart: () => {
              mediaComponentEl.style.setProperty('--l-media-content-position', 'fixed')
              mediaComponentEl.style.setProperty('--l-media-content-opacity', 1)
            },
            onReverseComplete: () => {
            },
            onComplete: () => {},
          }
        )
      }
		}
	)
}

/************************************************************************************

COMPONENT: SECTION SETUP OPTION CARDS

*************************************************************************************/
if (!customElements.get('l-setup-option-guide')) {
	customElements.define(
		'l-setup-option-guide',
		class LTASetupOptionGuide extends HTMLElement {
			constructor() {
				super()
			}

      connectedCallback() {
        this.triggerContentEls = this.querySelectorAll('[data-trigger-content]')
        this.overlayContentEl = this.querySelector('[data-overlay-content]')

        this.triggerContentEls.forEach((triggerContentEl) => {
          triggerContentEl.addEventListener('click', () => {
            this.overlayContentEl.classList.toggle('is-active')
          })
        })
			}
		}
	)
}

/************************************************************************************

COMPONENT: DESIGN GUIDES

*************************************************************************************/
if (!customElements.get('l-section-design-guides')) {
	customElements.define(
		'l-section-design-guides',
		class LTADesignGuides extends HTMLElement {
			constructor() {
				super()
			}

      connectedCallback() {
        // Elements
        this.filterTagEls = this.querySelectorAll('[data-filter-tag]')
        this.designCardEls = this.querySelectorAll('[data-card]')

        // Data
        this.currentTags = []

        this.filterTagEls.forEach((filterTagEl) => {
          filterTagEl.addEventListener('click', () => {
            const filterTag = filterTagEl.dataset.filterTag
            const isActive = filterTagEl.classList.contains('is-active')

            filterTagEl.classList.toggle('is-active', !isActive)

            isActive ? (this.currentTags = this.currentTags.filter(tag => tag !== filterTag)) : this.currentTags.push(filterTag);

            this.designCardEls.forEach((designCardEl) => {
              designCardEl.classList.toggle('l-hidden', this.currentTags.length > 0 && !this.currentTags.includes(designCardEl.dataset.card))
            })
          })
        })
			}
		}
	)
}

/************************************************************************************

SECTION: PRODUCT FEATURES

+ L-PRODUCT-FEATURES-CAROUSEL
+ L-PRODUCT-FEATURE-BOX

*************************************************************************************/
if (!customElements.get('l-product-feature-box')) {
	customElements.define(
		'l-product-feature-box',
		class LTAProductFeatures extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.hotspotEl = document.getElementById(this.dataset.hotspotId)

				// Events
				this.setupToggleEventOnDesktop()
				this.setupPosition()

				window.addEventListener(
					'resize',
					debounce(() => {
						this.classList.add('is-resizing')

						debounce(() => {
							this.setupPosition()
						}, 1000)()
					}, 20)
				)
			}

      setupToggleEventOnDesktop() {
        this.featureBoxes = this.parentElement.querySelectorAll('l-product-feature-box')
				this.currentContentEl = document.querySelector(
					`[data-feature-content="ProductFeatureContent-${this.dataset.blockId}"][data-media-query="(min-width: 1024px)"]`
				)

				if (!this.currentContentEl) return

				this.toggleButtonEl = this.currentContentEl.parentElement.querySelector('button')

				this.toggleButtonEl.addEventListener('click', () => {
					this.currentContentEl.classList.toggle('is-active')

          const isActive = this.currentContentEl.classList.contains('is-active')

          this.featureBoxes.forEach((featureBox) => {
            if (featureBox !== this) featureBox.toggleBoxStatus(false)
          })

					this.toggleBoxStatus(isActive)
				})
			}

			toggleBoxStatus(isActive) {
				this.currentContentEl.classList.toggle('is-active', isActive)
				this.toggleButtonEl && this.toggleButtonEl.classList.toggle('is-active', isActive)
				this.classList.toggle('is-active', isActive)
        this.hotspotEl.classList.toggle('is-active', isActive)

				this.setupPosition()
			}

			setupPosition() {
				// Get currentContentEl
				let mediaQuery = `(min-width: 1024px)`
				if (!window.matchMedia(mediaQuery).matches) {
					mediaQuery = `(max-width: 1023px)`
				}

				let currentContentEl = document.querySelector(
					`[data-feature-content="ProductFeatureContent-${this.dataset.blockId}"][data-media-query="${mediaQuery}"]`
				)

				if (!currentContentEl) return

				mediaQuery == `(min-width: 1024px)`
					? this.updateOnDesktop(currentContentEl)
					: this.updateOnMobile(currentContentEl)
			}

			updateOnMobile(currentContentEl) {
				// Get current position
				const contentRect = currentContentEl.getBoundingClientRect()
				const hotspotRect = this.hotspotEl.getBoundingClientRect()
				const containerRect = this.getBoundingClientRect()

				const positionBoxTop = Math.min(contentRect.top, hotspotRect.top) - containerRect.top
				let positionBoxLeft = Math.min(contentRect.left, hotspotRect.left) - containerRect.left
				const positionBoxWidth = Math.max(contentRect.right, hotspotRect.right) - positionBoxLeft - containerRect.left
				const positionBoxHeight = Math.abs(hotspotRect.top - contentRect.top)

				this.style.setProperty('--l-position-box-top', `${positionBoxTop}px`)
				this.style.setProperty('--l-position-box-left', `${positionBoxLeft}px`)
				this.style.setProperty('--l-position-box-width', `${positionBoxWidth - hotspotRect.width / 2}px`)
				this.style.setProperty('--l-position-box-height', `${positionBoxHeight + hotspotRect.width / 2}px`)

				// Determine which borders to show
				const showBottomBorder = contentRect.top <= hotspotRect.top

				// Set border properties
				this.style.setProperty('--l-top-border', '1px')
				this.style.setProperty('--l-left-border', '0')
				this.style.setProperty('--l-right-border', '1px')
				this.style.setProperty('--l-bottom-border', '0')

				this.style.setProperty('--l-bottom-border-width', `${contentRect.right - hotspotRect.right}px`)
				this.classList.toggle('is-mobile-bottom-border', showBottomBorder)

				this.classList.remove('is-resizing')
			}

			updateOnDesktop(currentContentEl) {
				// Get current position
				const contentRect = currentContentEl.getBoundingClientRect()
				const hotspotRect = this.hotspotEl.getBoundingClientRect()
				const containerRect = this.getBoundingClientRect()

				const positionBoxTop = Math.min(contentRect.top, hotspotRect.top) - containerRect.top
				let positionBoxLeft = Math.min(contentRect.left, hotspotRect.left) - containerRect.left
				const positionBoxWidth = Math.max(contentRect.right, hotspotRect.right) - positionBoxLeft - containerRect.left
				const positionBoxHeight = Math.abs(hotspotRect.top - contentRect.top)

				hotspotRect.left < contentRect.left
					? (positionBoxLeft += hotspotRect.width / 2 - 1)
					: (positionBoxLeft -= hotspotRect.width / 2 - 1)

				this.style.setProperty('--l-position-box-top', `${positionBoxTop}px`)
				this.style.setProperty('--l-position-box-left', `${positionBoxLeft}px`)
				this.style.setProperty('--l-position-box-width', `${positionBoxWidth}px`)
				this.style.setProperty('--l-position-box-height', `${positionBoxHeight}px`)

				// Determine which borders to show
				const showTopBorder = contentRect.top < hotspotRect.top
				const showLeftBorder = contentRect.left < hotspotRect.left
				const showRightBorder = contentRect.right > hotspotRect.right
        const showBottomBorder = contentRect.bottom > hotspotRect.bottom

        const positionClass = `${showTopBorder ? 'top' : ''}${showBottomBorder ? 'bottom' : ''}-${showLeftBorder ? 'left' : ''}${showRightBorder ? 'right' : ''}`
        this.classList.add(`is-${positionClass}`)

				// Set border properties
				this.style.setProperty('--l-top-border', showTopBorder ? '1px' : '0')
				this.style.setProperty('--l-right-border', showLeftBorder ? '1px' : '0')
				this.style.setProperty('--l-left-border', showLeftBorder ? '0' : showRightBorder ? '1px' : '0')
				this.style.setProperty('--l-bottom-border', showTopBorder ? '0' : showBottomBorder ? '1px' : '0')

				this.classList.remove('is-resizing')
			}
		}
	)
}

if (!customElements.get('l-product-features-carousel')) {
	customElements.define(
		'l-product-features-carousel',
		class LTAProductFeaturesCarousel extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Check if Swiper is already loaded
				if (window.Swiper) {
					this.setupCarousel()

					return
				}

				document.addEventListener('carousel:loaded', () => {
					this.setupCarousel()
				})
			}

			setupCarousel() {
				this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

				if (!this.carouselConfig) return

				this.classList.remove('is-placeholder')
				this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.swiperCarousel = new window.Swiper(this, this.carouselConfig)

				this.setActiveFeatureBox(0)

				this.swiperCarousel.on('slideChange', (swiper) => {
					this.setActiveFeatureBox(swiper.activeIndex)
				})
			}

			setActiveFeatureBox(activeIndex) {
				this.productFeaturesEl = this.productFeaturesEl || this.closest('[data-product-features]')
				this.featureBoxEls = this.productFeaturesEl.querySelectorAll('l-product-feature-box')

				this.featureBoxEls.forEach((featureBoxEl) => {
					const index = parseInt(featureBoxEl.dataset.index)

					featureBoxEl.setupPosition()
					featureBoxEl.toggleBoxStatus(index == activeIndex)
				})
			}

			slideTo(index = 0) {
				this.swiperCarousel.slideTo(index)
			}
		}
	)
}

/************************************************************************************
*************************************************************************************
*************************************************************************************

PRODUCT MODULE

*************************************************************************************
*************************************************************************************
*************************************************************************************/
if (!customElements.get('l-product-info')) {
	customElements.define(
		'l-product-info',
		class LProductInfo extends HTMLElement {
			quantityInput = undefined
			quantityForm = undefined
			onVariantChangeUnsubscriber = undefined
			cartUpdateUnsubscriber = undefined
			abortController = undefined
			pendingRequestUrl = null
			preProcessHtmlCallbacks = []
			postProcessHtmlCallbacks = []

			constructor() {
				super()
			}

			connectedCallback() {
				// Initialize the buttons state
				this.initializeProductSwapUtility()

				// Dispatch events
				this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }))

				// Catch variant picker options changed events
				this.onVariantChangeUnsubscriber = subscribe(
					PUB_SUB_EVENTS.optionValueSelectionChange,
					this.handleOptionValueChange.bind(this)
				)
			}

			disconnectedCallback() {
				this.onVariantChangeUnsubscriber()
				this.cartUpdateUnsubscriber?.()
			}

			addPreProcessCallback(callback) {
				this.preProcessHtmlCallbacks.push(callback)
			}

			initializeProductSwapUtility() {
				this.postProcessHtmlCallbacks.push((newNode) => {
					window?.Shopify?.PaymentButton?.init()
					window?.ProductModel?.loadShopifyXR()
				})
			}

			handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
				if (!this.contains(event.target)) return

				// Reset form data
				this.resetProductFormState()

				// Check new selected status
				const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
				this.pendingRequestUrl = productUrl
				const shouldSwapProduct = this.dataset.url !== productUrl
				const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct

				// Render product information based on new selected options
				this.renderProductInfo({
					requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
					targetId: target.id,
					callback: shouldSwapProduct
						? this.handleSwapProduct(productUrl, shouldFetchFullPage)
						: this.handleUpdateProductInfo(productUrl),
				})
			}

			resetProductFormState() {
				const productForm = this.productForm
				productForm?.toggleSubmitButton(true, null, false)
				productForm?.handleErrorMessage()
			}

			renderProductInfo({ requestUrl, targetId, callback }) {
				this.abortController?.abort()
				this.abortController = new AbortController()

				fetch(requestUrl, { signal: this.abortController.signal })
					.then((response) => response.text())
					.then((responseText) => {
						this.pendingRequestUrl = null
						const html = new DOMParser().parseFromString(responseText, 'text/html')

						callback(html)
					})
					.then(() => {
						// set focus to last clicked option value
						const focusableEle = document.querySelector(`#${targetId}`)

						focusableEle?.focus()
					})
					.catch((error) => {
						console.error(error)

						if (error.name === 'AbortError') {
							console.log('Fetch aborted by user')
						} else {
							console.error(error)
						}
					})
			}

			handleSwapProduct(productUrl, updateFullPage) {
				return (html) => {
					const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info'
					const variant = this.getSelectedVariant(html.querySelector(selector))
					this.updateURL(productUrl, variant?.id)

					if (updateFullPage) {
						document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML

						HTMLUpdateUtility.viewTransition(
							document.querySelector('main'),
							html.querySelector('main'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					} else {
						HTMLUpdateUtility.viewTransition(
							this,
							html.querySelector('product-info'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					}
				}
			}

			handleUpdateProductInfo(productUrl) {
				return (html) => {
					const variant = this.getSelectedVariant(html)

					this.updateOptionValues(html)
					this.updateURL(productUrl, variant?.id)
					this.updateVariantInputs(variant?.id)

					if (!variant) {
						this.setUnavailable()

						return
					}

					const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
						const source = html.getElementById(`${id}-${this.sectionId}`)
						const destination = this.querySelector(`#${id}-${this.dataset.section}`)

						if (source && destination) {
							destination.innerHTML = source.innerHTML
							destination.classList.toggle('hidden', shouldHide(source))
						}
					}

					updateSourceFromDestination('price')
					updateSourceFromDestination('ProductSubmitButton')
					updateSourceFromDestination('media-atc')

					this.productForm?.toggleSubmitButton(
						html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
						window.theme.strings.sold_out,
						false
					)

					publish(PUB_SUB_EVENTS.variantChange, {
						data: {
							sectionId: this.sectionId,
							html,
							variant,
						},
					})
				}
			}

			updateOptionValues(html) {
				const variantSelects = html.querySelector('l-variant-selects')

				if (variantSelects) {
					HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks)
				}
			}

			updateURL(url, variantId) {
				if (this.dataset.updateUrl === 'false') return

				window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`)
			}

			updateVariantInputs(variantId) {
				this.querySelectorAll(
					`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
				).forEach((productForm) => {
					const input = productForm.querySelector('input[name="id"]')
					input.value = variantId ?? ''
					input.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}

			setUnavailable() {
				this.productForm?.toggleSubmitButton(true, window.theme.strings.unavailable)

				const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
					.map((id) => `#${id}-${this.dataset.section}`)
					.join(', ')

				document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'))
			}

			getSelectedVariant(productInfoNode) {
				const selectedVariant = productInfoNode.querySelector('l-variant-selects [data-selected-variant]')?.innerHTML
				return !!selectedVariant ? JSON.parse(selectedVariant) : null
			}

			buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
				const params = []

				!shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

				if (optionValues.length) {
					params.push(`option_values=${optionValues.join(',')}`)
				}

				return `${url}?${params.join('&')}`
			}

			get productForm() {
				return this.querySelector('l-product-form')
			}

			get variantSelectors() {
				return this.querySelector('l-variant-selects')
			}

			get sectionId() {
				return this.dataset.originalSection || this.dataset.section
			}
		}
	)
}

/************************************************************************************

PRODUCT INFORMATION COMPONENTS
+ l-variant-selects
+ l-product-controls
+ l-product-quantity-input
+ l-product-delivery-estimation

*************************************************************************************/
if (!customElements.get('l-product-form')) {
	customElements.define(
		'l-product-form',
		class LProductForm extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.form = this.querySelector('form')
				this.cart = document.getElementById('nav-drawer')
				this.submitButton = this.querySelector('[type="submit"]')
				this.submitButtonText = this.submitButton.querySelector('[data-add-to-cart-label]')

				// Set data / attribute
				this.sectionId = this.dataset.sectionId
				this.hideErrors = this.dataset.hideErrors === 'true'

				// Setup event
				this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
			}

			onSubmitHandler(evt) {
				evt.preventDefault()

				if (this.submitButton.getAttribute('aria-disabled') === 'true') return

				// Setup button loading state
				this.submitButton.setAttribute('aria-disabled', true)
				this.submitButton.classList.add('is-loading')

				// Setup product form data
				const config = fetchConfig('javascript')
				config.headers['X-Requested-With'] = 'XMLHttpRequest'
				delete config.headers['Content-Type']

				const formData = new FormData(this.form)

				config.body = formData

				// Fetch API to ATC
				fetch(`${window.theme.routes.cart_add_url}`, config)
					.then((response) => response.json())
					.then((response) => {
						// ATC: Handle error case
						if (response.status) {
							publish(PUB_SUB_EVENTS.cartError, {
								source: 'product-form',
								productVariantId: formData.get('id'),
								errors: response.errors || response.description,
								message: response.message,
							})

							// Show error message + Show sold out label
							this.handleErrorMessage(response.description)

							// Toggle sold-out label
							const soldOutMessage = this.submitButton.querySelector('[data-sold-out-label]')
							if (soldOutMessage) {
								this.submitButton.setAttribute('aria-disabled', true)
								this.submitButtonText.classList.add('hidden')
								soldOutMessage.classList.remove('hidden')
								this.error = true
							}

							return
						} else if (!this.cart) {
							// Redirect to cart page if cart-drawer equal to null
							window.location = window.theme.routes.cart_url

							return
						}

						window.getCart?.()
						window.scrollToCartTop?.()

						publish(PUB_SUB_EVENTS.cartUpdate, {
							source: 'product-form',
							productVariantId: formData.get('id'),
							cartData: response,
						})

						document.dispatchEvent(
							new CustomEvent('theme:cart:add', {
								bubbles: true,
								detail: {
									selector: this.submitButton,
								},
							})
						)

						this.error = false
					})
					.catch((e) => {
						console.error(e)
					})
					.finally(() => {
						// Remove button loading state
						this.submitButton.classList.remove('is-loading')
						if (!this.error) this.submitButton.removeAttribute('aria-disabled')
					})
			}

			handleErrorMessage(errorMessage = false) {
				if (this.hideErrors) return

				if (!this.errorMessageWrapper) {
					this.errorMessageWrapper = document.getElementById(`ProductErrorMessages-${this.sectionId}`)
					this.errorMessage = this.errorMessageWrapper.querySelector('[data-error-message]')
				}

				if (!this.errorMessageWrapper) return

				this.errorMessageWrapper.classList.toggle('hidden', !errorMessage)
				if (errorMessage) this.errorMessage.textContent = errorMessage

				debounce(() => {
					this.errorMessageWrapper.classList.add('hidden')
				}, 4000)()
			}

			// This is global function
			toggleSubmitButton(disable = true, text, needToUpdateText = true) {
				if (disable) {
					this.submitButton.setAttribute('disabled', 'disabled')
					if (text) this.submitButtonText.textContent = text

					return
				}

				this.submitButton.removeAttribute('disabled')

				if (needToUpdateText) {
					this.submitButtonText.textContent = window.theme.strings.add_to_cart
				}
			}

			get variantIdInput() {
				return this.form.querySelector('[name=id]')
			}
		}
	)
}

if (!customElements.get('l-variant-selects')) {
	customElements.define(
		'l-variant-selects',
		class LTAVariantSelect extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.addEventListener('change', (event) => {
					const target = this.getInputForEventTarget(event.target)

					this.updateSelectionMetadata(event)

					publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
						data: {
							event,
							target,
							selectedOptionValues: this.selectedOptionValues,
						},
					})
				})
			}

			updateSelectionMetadata({ target }) {
				const { value, tagName } = target

				if (tagName === 'SELECT' && target.selectedOptions.length) {
					return
				}

				// Update selected option value
				if (target.hasAttribute('data-product-variant-input')) {
					const selectedSwatchValue = target.closest('[data-variant-options]').querySelector('[data-selected-value]')
					if (selectedSwatchValue) selectedSwatchValue.innerHTML = value
				}
			}

			getInputForEventTarget(target) {
				return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
			}

			get selectedOptionValues() {
				return Array.from(
					this.querySelectorAll('[data-variant-select] option[selected], [data-product-variant-input]:checked')
				).map(({ dataset }) => dataset.optionValueId)
			}
		}
	)
}

if (!customElements.get('l-product-quantity-input')) {
	customElements.define(
		'l-product-quantity-input',
		class LTAProductQuantityInput extends HTMLElement {
			constructor() {
				super()
			}

			quantityUpdateUnsubscriber = undefined

			connectedCallback() {
				// Elements
				this.input = this.querySelector('input')

				// Setup events
				this.changeEvent = new Event('change', { bubbles: true })

				this.input.addEventListener('change', () => {
					this.validateQtyRules()
				})

				this.querySelectorAll('button').forEach((button) =>
					button.addEventListener('click', this.onButtonClick.bind(this))
				)

				// Subscribe global event
				this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this))

				// Validate Quantity Rules
				this.validateQtyRules()
			}

			disconnectedCallback() {
				if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber()
			}

			onButtonClick(event) {
				event.preventDefault()

				const previousValue = this.input.value

				if (event.target.name === 'plus') {
					// Reset value to minimum value || increase value based on step
					const needToResetMinimumValue =
						parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0

					needToResetMinimumValue ? (this.input.value = this.input.dataset.min) : this.input.stepUp()
				} else {
					this.input.stepDown()
				}

				// Dispatch event
				if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent)

				// Reset to minimum value
				if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
					this.input.value = parseInt(this.input.min)
				}
			}

			validateQtyRules() {
				const value = parseInt(this.input.value)

				if (this.input.min) {
					const buttonMinus = this.querySelector("button[name='minus']")
					buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min))
				}

				if (this.input.max) {
					const max = parseInt(this.input.max)
					const buttonPlus = this.querySelector("button[name='plus']")
					buttonPlus.classList.toggle('disabled', value >= max)
				}
			}
		}
	)
}

if (!customElements.get('l-product-controls')) {
	customElements.define(
		'l-product-controls',
		class LProductInfo extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.quantityFormEl = this.querySelector('l-product-quantity-input')
				this.quantityInputEl = this.quantityFormEl?.querySelector('input')
				this.productForm = this.querySelector('l-product-form')

				if (!this.quantityInputEl || !this.quantityFormEl) return

				// Catch global event

				// Subscribe global event
				this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.updateQuantityRules.bind(this))

				// Setup validate quantity controls
				this.setQuantityBoundries()
			}

			disconnectedCallback() {
				if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber()
			}

			setQuantityBoundries() {
				// Get data from l-product-quantity-input element
				const quantityDataset = this.quantityInputEl.dataset
				const data = {
					cartQuantity: quantityDataset.cartQuantity ? parseInt(quantityDataset.cartQuantity) : 0,
					min: quantityDataset.min ? parseInt(quantityDataset.min) : 1,
					max: quantityDataset.max ? parseInt(quantityDataset.max) : null,
					step: this.quantityInputEl.step ? parseInt(this.quantityInputEl.step) : 1,
				}

				// Get min-max quantity of selected variant based on variant in current cart
				let min = data.min
				const max = data.max === null ? data.max : data.max - data.cartQuantity
				if (max !== null) min = Math.min(min, max)
				if (data.cartQuantity >= data.min) min = Math.min(min, data.step)

				// Set values for quantity input element (min, max, value)
				this.quantityInputEl.min = min
				this.quantityInputEl.value = min
				max ? (this.quantityInputEl.max = max) : this.quantityInputEl.removeAttribute('max')

				// Publish quantity update
				publish(PUB_SUB_EVENTS.quantityUpdate, undefined)
			}

			updateQuantityRules({ data }) {
				const { html, sectionId } = data

				const quantityFormUpdated = html.getElementById(`Product-Form-Controls-${sectionId}`)
				const selectors = ['[data-product-quantity-input]', '[data-quantity-label]']

				for (let selector of selectors) {
					const current = this.querySelector(selector)
					const updated = quantityFormUpdated.querySelector(selector)
					if (!current || !updated) continue

					// Update product quantity input || other elements
					if (selector === '[data-product-quantity-input]') {
						const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step']
						for (let attribute of attributes) {
							const valueUpdated = updated.getAttribute(attribute)
							valueUpdated !== null ? current.setAttribute(attribute, valueUpdated) : current.removeAttribute(attribute)
						}
					} else {
						current.innerHTML = updated.innerHTML
					}
				}

				// Update and dispatch quantity changed event
				this.setQuantityBoundries()
			}

			// b2b__fetchQuantityRules() {
			// 	// Get current selected variant ID
			// 	const currentVariantId = this.productForm?.variantIdInput?.value
			// 	if (!currentVariantId) return

			// 	// Call API to update rules cart component
			// 	this.querySelector('[data-rules-cart]').classList.add('is-loading')

			// 	fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
			// 		.then((response) => response.text())
			// 		.then((responseText) => {
			// 			const html = new DOMParser().parseFromString(responseText, 'text/html')
			// 			this.updateQuantityRules(this.dataset.section, html)
			// 		})
			// 		.catch((e) => console.error(e))
			// 		.finally(() => this.querySelector('[data-rules-cart]').classList.remove('is-loading'))
			// }
		}
	)
}

if (!customElements.get('l-product-delivery-estimation')) {
	customElements.define(
		'l-product-delivery-estimation',
		class LTADeliveryEstimation extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.minDeliveryDateEl = this.querySelector('[data-min-delivery-text]')
				this.maxDeliveryDateEl = this.querySelector('[data-max-delivery-text]')

				this.minDays = parseInt(this.dataset.minDays)
				this.maxDays = parseInt(this.dataset.maxDays)

				const today = new Date()

				if (this.minDeliveryDateEl) {
					this.minDeliveryDateEl.innerHTML = this.getNextWorkingDays(today, this.minDays)
				}

				if (this.maxDeliveryDateEl) {
					this.maxDeliveryDateEl.innerHTML = this.getNextWorkingDays(today, this.maxDays)
				}
			}

			getNextWorkingDays(date, daysToAdd) {
				let nextDay = new Date(date)
				let addedDays = 0

				while (addedDays < daysToAdd) {
					nextDay.setDate(nextDay.getDate() + 1)

					if (nextDay.getDay() !== 0 && nextDay.getDay() !== 6) {
						addedDays++
					}
				}

				return this.formatDate(nextDay)
			}

			formatDate(date) {
				const day = date.getDate()
				const month = date.toLocaleString('en-GB', { month: 'short' })

				return `${day} ${month}`
			}
		}
	)
}

if (!customElements.get('l-product-block-rating-email')) {
	customElements.define(
		'l-product-block-rating-email',
		class LTARatingEmail extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
        this.subscriptionFormTriggerEl = this.querySelector('[data-subscription-form]')

        if (!this.subscriptionFormTriggerEl) return

				// Data
        const formId = this.dataset.klaviyoFormId || window.theme.settings.klaviyoForm || 'YwWLVU'


        this.subscriptionFormTriggerEl.addEventListener('click', (event) => {
          event.preventDefault()

          window._klOnsite = window._klOnsite || []
          window._klOnsite.push(['openForm', formId])
        })
			}

			onSubscriptionFormTrigger(event) {
				event.preventDefault()

				window._klOnsite = window._klOnsite || []
				const formID = window.matchMedia('(max-width: 749px)').matches ? this.mobileFormId : this.desktopFormId

				window._klOnsite.push(['openForm', formID])
			}
		}
	)
}

if (!customElements.get('l-product-sticky-atc')) {
	customElements.define(
		'l-product-sticky-atc',
		class LTAProductStickyATC extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
        // Data
        this.sectionId = this.dataset.sectionId

        // Elements
        this.submitBtnEl = this.querySelector('[data-atc]')
        this.realSubmitBtnEl = document.getElementById(`ProductSubmitButton-${this.sectionId}`)

				// Click event
				this.submitBtnEl.addEventListener('click', () => {
					this.submitBtnEl.classList.add('is-loading')

					debounce(() => {
						this.submitBtnEl.classList.remove('is-loading')
					}, 3000)()
				})

				// Cart update event
				this.productAddedToCart = subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
					this.submitBtnEl.classList.remove('is-loading')
        })

        this.handleScrollEvent()
			}

			disconnectedCallback() {
				this.productAddedToCart()
      }

      handleScrollEvent() {
        const handleScrollEvent = () => {
          const rect = this.realSubmitBtnEl.getBoundingClientRect();
          const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

          this.classList.toggle('is-active', !isVisible);
        };

        window.addEventListener('scroll', debounce(handleScrollEvent, 100));
        handleScrollEvent();
      }
		}
	)
}

if (!customElements.get('l-product-media-gallery')) {
	customElements.define(
		'l-product-media-gallery',
		class ProductMediaGallery extends HTMLElement {
			constructor() {
				super()

				this.sectionId = this.dataset.sectionId
			}

			productVariantLoaded = undefined

			connectedCallback() {
				// Check if Swiper is already loaded
				if (window.Swiper) {
					this.setupCarousel()
				} else {
					document.addEventListener('carousel:loaded', () => {
						this.setupCarousel()
					})
				}

				new window.mediaZoom(this)

				this.productVariantLoaded = subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
					this.onVariantChange(event.data)
				})
			}

			disconnectedCallback() {
				if (this.productVariantLoaded) {
					this.productVariantLoaded()
				}
			}

			setupCarousel() {
				/*** Thumbnails carousel ***/
				this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
				if (this.thumbnailCarousel) {
					this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')
					this.thumbnailNavigationNext = this.thumbnailCarousel.querySelector('[data-next]')

					this.thumbnailSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

					this.thumbnailCarouselSwiper = new window.Swiper(this.thumbnailCarousel, {
						loop: false,
						slidesPerView: 'auto',
						spaceBetween: 15,
						direction: 'vertical',
						navigation: {
							nextEl: this.thumbnailNavigationNext,
						},
					})
				}

				/*** ThumbMain carousel setup ***/
				this.mainCarousel = this.querySelector('[data-main-carousel]')
				this.mediaSlides = this.mainCarousel.querySelectorAll('[data-media]')
				this.navigationNext = this.mainCarousel.querySelector('[data-next]')
				this.navigationPrev = this.mainCarousel.querySelector('[data-prev]')

				this.mediaSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.mainCarouselSwiper = new window.Swiper(this.mainCarousel, {
					loop: false,
					slidesPerView: 'auto',
					keyboard: {
						enabled: true,
					},
					pagination: {
						el: `#MobilePagination-${this.dataset.sectionId}`,
						clickable: true,
						dynamicBullets: true,
					},
					...(this.thumbnailCarouselSwiper && {
						thumbs: {
							swiper: this.thumbnailCarouselSwiper,
						},
					}),
				})

				/* Setup controller */
				if (this.thumbnailCarouselSwiper) {
					this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper
				}

				// Events
				this.mainCarouselSwiper.on('slideChangeTransitionEnd', () => {
					const activeSlideVideoEl = this.querySelector('.swiper-slide-active video')

					if (!activeSlideVideoEl) return

					activeSlideVideoEl.play()
				})
			}

			getVariantMediaData() {
				return JSON.parse(this.querySelector('.js-product-variants').textContent) || null
			}

			onPlayVideo() {
				this.videoEls = this.querySelectorAll('video')

				this.videoEls.forEach((videoEl) => {
					videoEl.pause()
				})
			}

			onVariantChange(data) {
				this.variantData = this.variantData || this.getVariantMediaData()
				const selectedVariantId = data.variant.id

				const matchMediaId = this.variantData.find((variant) => variant.id == selectedVariantId)?.featured_media?.id

				this.mediaSlides.forEach((mediaSlide, index0) => {
					const mediaId = mediaSlide.dataset.media

					if (mediaId == matchMediaId) {
						this.mainCarouselSwiper.slideTo(index0, true)
					}
				})
			}
		}
	)
}

if (!customElements.get('l-product-back-in-stock-form')) {
	class LTAProductBackInStockForm extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Elements
			this.formEl = this.querySelector('form')
			this.successTextEl = this.querySelector('[data-success-text]')
      this.errorTextEl = this.querySelector('[data-error-text]')
      this.submitBtnEl = this.querySelector('[data-submit]')
      this.emailInputEl = this.querySelector('[data-email-input]')

			// Data
			this.selectedVariantId = this.dataset.selectedVariantId
      this.publicKlavyioId = this.dataset.klavyioId

      console.log({
        selectedVariantId: this.selectedVariantId,
        publicKlavyioId: this.publicKlavyioId
      });

			// Events
			this.formEl.addEventListener('submit', this.handleSubmitEvent.bind(this))
      this.productVariantLoaded = subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        this.selectedVariantId = event.data.variant.id
      })
    }

		handleSubmitEvent(event) {
      event.preventDefault()
      this.submitBtnEl.classList.add('is-loading')

			const email = this.emailInputEl.value
			// const phone = formData.get('phone')

      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                },
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${this.selectedVariantId}`,
              },
            },
          },
        },
      }

			const requestOptions = {
				method: 'POST',
				headers: {
          "Content-Type": "application/json",
          "revision":"2024-06-15"
				},
				body: JSON.stringify(payload),
      }

      console.log(payload);
      console.log(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.publicKlavyioId}`);

			fetch(
				`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.publicKlavyioId}`,
				requestOptions
			)
        .then((response) =>
        {
          console.log(response);

					// Success
					if (response.status == 202) {
						this.successTextEl.classList.remove('l-hidden')

						debounce(() => {
							this.successTextEl.classList.add('l-hidden')
						}, 5000)()
          } else {
            this.errorTextEl.classList.remove('l-hidden')

						debounce(() => {
							this.errorTextEl.classList.add('l-hidden')
						}, 5000)()
          }
        })
				.catch((error) => {
					console.log(error)
        })
      .finally(() => {
        this.formEl.reset()
        this.submitBtnEl.classList.remove('is-loading')
      })
		}
	}

	customElements.define('l-product-back-in-stock-form', LTAProductBackInStockForm)
}

if (!customElements.get('l-product-back-in-stock-button')) {
  class LTAProductBackInStockButton extends HTMLElement {
    connectedCallback() {
      this.productVariantLoaded = subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        const isAvailable = event.data.variant.available

        console.log(event.data.variant);
        console.log(isAvailable);


        this.classList.toggle('l-hidden', isAvailable)
      })
    }
  }
	customElements.define('l-product-back-in-stock-button', LTAProductBackInStockButton)
}

/************************************************************************************

COMPONENTS: QUICK VIEW

*************************************************************************************/
if (!customElements.get('l-quick-view-media')) {
	customElements.define(
		'l-quick-view-media',
		class LTAQuickViewMedia extends HTMLElement {
			constructor() {
				super()
			}

			productVariantLoaded = undefined

			connectedCallback() {
				this.variantMediaEls = this.querySelectorAll('[data-variant-media]')

				this.productVariantLoaded = subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
					this.onVariantChange(event.data)
				})
			}

			onVariantChange(data) {
				const selectedVariantId = data.variant.id

				this.variantMediaEls.forEach((variantMediaEl) => {
					const variantId = parseInt(variantMediaEl.dataset.variantMedia)

					variantMediaEl.classList.toggle('l-hidden', variantId != selectedVariantId)
				})
			}
		}
	)
}

if (!customElements.get('l-quick-view-drawer')) {
	customElements.define(
		'l-quick-view-drawer',
		class LTAQuickViewDrawer extends HTMLElement {
			constructor() {
				super()
			}

			productVariantLoaded = undefined

			connectedCallback() {
				this.productVariantLoaded = subscribe(PUB_SUB_EVENTS.quickView.open, (event) => {
					this.renderProductData(event.productUrl)
				})

				this.cartUpdateLoaded = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
					this.close()
				})
			}

			disconnectedCallback() {
				this.productVariantLoaded()
				this.cartUpdateLoaded()
			}

			renderProductData(productUrl) {
				const url = `${productUrl}?section_id=${this.dataset.sectionId}`

				fetch(url)
					.then((response) => response.text())
					.then((text) => {
						const html = document.createElement('div')
						html.innerHTML = text
						const quickViewContainer = html.querySelector('#QuickViewContainer')

						if (quickViewContainer) {
							this.innerHTML = quickViewContainer.innerHTML

							this.open()
						}
					})
					.catch((e) => {
						console.error(e)
					})
					.finally(() => {
						this.setupEvent()
					})
			}

			setupEvent() {
				this.closeButtonEl = this.querySelector('[data-quick-view-close]')
				this.productInfoEl = this.querySelector('l-product-info')

				this.addEventListener('click', (event) => {
					if (!this.productInfoEl.contains(event.target)) {
						this.close()
					}
				})

				this.closeButtonEl.addEventListener('click', () => {
					this.close()
				})
			}

			open() {
				this.classList.remove('l-hidden')

				requestAnimationFrame(() => {
					this.classList.add('is-active')
					lockBodyScroll()
				})
			}

			close() {
				this.classList.remove('is-active')

				debounce(() => {
					this.classList.add('l-hidden')
					unLockBodyScroll()
				}, 800)()
			}
		}
	)
}

/************************************************************************************

* CUSTOMIZE FUNCTIONALITIES

*************************************************************************************/
window.addEventListener('DOMContentLoaded', () => {
	/****************

  COMPONENT: HEADER

  *****************/
	function setupHeaderMobileDrawer() {
		const headerMobileDrawerEl = document.getElementById('nav-drawer')
		const subMenuLinkEls = headerMobileDrawerEl.querySelectorAll('[data-menu-trigger]')
		const backLinkEls = headerMobileDrawerEl.querySelectorAll('[data-menu-back]')
		const closeDrawerEl = headerMobileDrawerEl.querySelector('[data-drawer-toggle]')
		const closeBtnEls = headerMobileDrawerEl.querySelectorAll('[data-menu-close]')

		// Trigger menu drawer
		subMenuLinkEls.forEach((subMenuLinkEl) => {
			subMenuLinkEl.addEventListener('click', (event) => {
				event.preventDefault()

				const subMenuLinkId = subMenuLinkEl.dataset.menuTrigger
				const subMenuEl = document.getElementById(subMenuLinkId)

				subMenuEl.classList.add('is-menu-active')
			})
		})

		// Close current menu drawer
		backLinkEls.forEach((backLinkEl) => {
			backLinkEl.addEventListener('click', (event) => {
				event.preventDefault()

				const subMenuEl = backLinkEl.closest('[id^="MobileNav-"]')
				subMenuEl.classList.remove('is-menu-active')
			})
		})

		// Fake close data-menu-close
		closeBtnEls.forEach((closeBtnEl) => {
			closeBtnEl.addEventListener('click', () => {
				closeDrawerEl.dispatchEvent(new Event('click', { bubbles: true }))
			})
		})

		// Update cart-count after product added to cart
		const cartCountEls = document.querySelectorAll('[data-cart-count]')
		const updateCartCountEvents = ['theme:cart:update', 'theme:cart:added', 'rebuy:cart.add', 'rebuy:cart.change']
		updateCartCountEvents.forEach((event) => {
      document.addEventListener(event, () => {
				fetch(window.theme.routes.cart_url + '.js')
					.then((response) => response.json())
					.then((cart) => {
						cartCountEls.forEach((cartCountEl) => (cartCountEl.textContent = cart.item_count))
					})
					.catch((error) => {
						console.error('Error:', error)
					})
			})
		})
	}

	/****************

  COMPONENT: COLLECTION PAGE

  *****************/
	function setupCollectionPage() {
    const collectionPageEl = document.getElementById('CollectionPage') || document.getElementById('SearchPage')

		if (!collectionPageEl) return

		const toggleBtnEl = collectionPageEl.querySelector('[data-toggle-filters]')
		const filterDrawer = collectionPageEl.querySelector('[data-collection-filters]')
		const filterDrawerWrapper = collectionPageEl.querySelector('[data-collection-filters-wrapper]')

		toggleBtnEl.addEventListener('click', () => {
			if (window.matchMedia('(min-width: 768px)').matches) {
				filterDrawer.classList.add('collection__filters--visible')
			}
		})

		const onClickOutSiteFilterDrawer = (event) => {
			if (!filterDrawerWrapper.contains(event.target)) {
				filterDrawer.classList.remove('collection__filters--visible')
			}
		}

		filterDrawer.addEventListener('click', onClickOutSiteFilterDrawer)

		document.addEventListener('theme:filters:init', () => {
			filterDrawer.removeEventListener('click', onClickOutSiteFilterDrawer)

			setupCollectionPage()
		})
	}

	/****************

  COMPONENT: ANIMATION ON SCREEN

  *****************/
	function animationOnScreen(params) {
		const animationEls = document.querySelectorAll('[data-screen-animation-element]')

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					entry.target.classList.toggle('in-screen', entry.isIntersecting)
				})
			},
			{ threshold: 0.1 }
		)

		animationEls.forEach((el) => {
			observer.observe(el)
		})
  }

  /****************

  COMPONENT: RATING EMAIL

  *****************/
  function triggerSubscriptionForm() {
    const subscriptionTriggerEls = document.querySelectorAll('a[href="#subscription-form"]')
    const mobileFormId = window.theme.settings.klaviyoForm || 'YwWLVU'
    const desktopFormId = window.theme.settings.klaviyoForm || 'YwWLVU'


    subscriptionTriggerEls.forEach((subscriptionTriggerEl) => {
      subscriptionTriggerEl.addEventListener('click', (event) => {
        event.preventDefault()

				window._klOnsite = window._klOnsite || []
				const formID = window.matchMedia('(max-width: 749px)').matches ? mobileFormId : desktopFormId

				window._klOnsite.push(['openForm', formID])
      })
    })
  }

	// Run functions
	setupHeaderMobileDrawer()
	animationOnScreen()
  setupCollectionPage()
  triggerSubscriptionForm()
})

